import React, { useState, useEffect, useRef, useCallback } from 'react';

function AutocompleteInput({ value, onChange, onKeyDown, placeholder, fetchSuggestions }) {
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const wrapperRef = useRef(null);
    const debounceRef = useRef(null);

    const loadSuggestions = useCallback(async (query) => {
        if (!query || query.length < 1) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }
        try {
            const res = await fetchSuggestions(query);
            const data = res.data || [];
            setSuggestions(data);
            setShowDropdown(data.length > 0);
            setActiveIndex(-1);
        } catch {
            setSuggestions([]);
            setShowDropdown(false);
        }
    }, [fetchSuggestions]);

    const handleInputChange = (e) => {
        const val = e.target.value;
        onChange(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => loadSuggestions(val), 300);
    };

    const handleSelect = (item) => {
        onChange(item);
        setShowDropdown(false);
        setSuggestions([]);
    };

    const handleKeyDownInner = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            if (activeIndex >= 0 && activeIndex < suggestions.length) {
                handleSelect(suggestions[activeIndex]);
                return;
            }
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
        }
        if (onKeyDown) onKeyDown(e);
    };

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    return (
        <div className="relative" ref={wrapperRef}>
            <input
                type="text"
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDownInner}
                onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
                placeholder={placeholder}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            {showDropdown && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg">
                    <ul className="max-h-52 overflow-y-auto overflow-x-auto">
                        {suggestions.map((item, idx) => (
                            <li
                                key={idx}
                                onClick={() => handleSelect(item)}
                                className={`px-3 py-2 text-sm cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors whitespace-nowrap ${
                                    idx === activeIndex ? 'bg-blue-100 text-blue-700 font-medium' : 'hover:bg-gray-50'
                                }`}
                                title={item}
                            >
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default AutocompleteInput;
